import { AfterContentInit, Component, ContentChildren, inject, Input, OnChanges, QueryList, Renderer2, SimpleChanges } from '@angular/core';
import { HighlightMessageTrayDirective, HighlightMessageTrayRole } from './highlight-message-tray.directive';

export type HighlightMessageVariant =
  | ''
  | 'warning'
  | 'info'
  | 'highlight'
  | 'error'
  | 'positive'
  | 'evi'
  | 'evi-lg'
  | 'impact'
  | 'uw'
  | 'uw-condition';

@Component({
  selector: 'app-highlight-message',
  templateUrl: './highlight-message.component.html',
  standalone: true
})
export class HighlightMessageComponent implements AfterContentInit, OnChanges {
  @Input() variant: HighlightMessageVariant = '';
  @Input() hideIcon = false;
  @Input() icon = '';
  @Input() iconImage = '';
  @Input() iconImageAlt = '';
  @Input() title = '';
  @Input() message = '';
  @Input() additionalClasses = '';
  @Input() id = '';
  @ContentChildren(HighlightMessageTrayDirective)
  trayContent!: QueryList<HighlightMessageTrayDirective>;

  private readonly renderer = inject(Renderer2);

  get iconHref(): string {
    const icon = this.icon || this.defaultIcon;
    return icon ? (icon.startsWith('#') ? icon : `#${icon}`) : '';
  }

  get defaultIcon(): string {
    switch (this.variant) {
      case 'info':
      case 'highlight':
      case 'positive':
        return 'info';
      case 'error':
        return 'warning';
      default:
        return 'warning';
    }
  }

  get hasTrayContent(): boolean {
    return Boolean(this.trayContent?.length);
  }

  get trayVariant(): string {
    if (this.variant === 'evi-lg') {
      return 'evi';
    }

    return this.variant || 'warning';
  }

  ngAfterContentInit(): void {
    this.syncTrayVariationClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.variant) {
      this.syncTrayVariationClasses();
    }
  }

  private syncTrayVariationClasses(): void {
    if (!this.trayContent) {
      return;
    }

    this.trayContent.forEach(trayItem => {
      const element = trayItem.elementRef.nativeElement;
      this.variationClasses(trayItem.normalizedRole).forEach(className => {
        this.renderer.removeClass(element, className);
      });
      this.renderer.addClass(element, this.variationClass(trayItem.normalizedRole));
    });
  }

  private variationClasses(role: HighlightMessageTrayRole): string[] {
    const variants = ['warning', 'info', 'highlight', 'error', 'positive', 'evi'];

    switch (role) {
      case 'secondary':
        return variants.map(variant => `button--secondary-${variant}`);
      case 'indicator':
        return variants.map(variant => `indicator--${variant}`);
      case 'label':
        return variants.map(variant => `tag-pill--${variant}-inverted`);
      default:
        return variants.map(variant => `button--${variant}`);
    }
  }

  private variationClass(role: HighlightMessageTrayRole): string {
    switch (role) {
      case 'secondary':
        return `button--secondary-${this.trayVariant}`;
      case 'indicator':
        return `indicator--${this.trayVariant}`;
      case 'label':
        return `tag-pill--${this.trayVariant}-inverted`;
      default:
        return `button--${this.trayVariant}`;
    }
  }

  get noticeClasses(): string {
    return ['notice', this.variant && `notice--${this.variant}`, this.additionalClasses]
      .filter(Boolean)
      .join(' ');
  }
}
